import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { I2faSetupResponse } from '../../../../../../../modules/API/Auth/models/authDataModel'
import OtpCodeInput from '../../../../../../../generic/ui/otp-code-input/OtpCodeInput'
import XIcon from '../../../../../../../generic/icons/IconLib/XIcon'
import CopyIcon from '../../../../../../../generic/icons/IconLib/CopyIcon'
import ShieldLibIcon from '../../../../../../../generic/icons/IconLib/ShieldIcon'
import SpinnerIcon from '../../../../../../../generic/icons/IconLib/SpinnerIcon'

type Props = {
  setupData: I2faSetupResponse
  onConfirm: (code: string) => Promise<void>
  onClose: () => void
  isLoading: boolean
}

const CODE_LENGTH = 6

const QrSetupModal = ({ setupData, onConfirm, onClose, isLoading }: Props) => {
  const [code, setCode] = useState<string>('')

  const isComplete = code.length === CODE_LENGTH

  const handleSubmit = useCallback((): void => {
    if (code.length < CODE_LENGTH) return
    onConfirm(code)
  }, [code, onConfirm])

  return (
    <Overlay role="dialog" aria-modal="true" aria-label="הפעלת אימות דו-שלבי">
      <Card>
        <CloseBtn
          onClick={onClose}
          aria-label="סגור"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && onClose()}
        >
          <XIcon size={16} />
        </CloseBtn>

        <Header>
          <ShieldIcon><ShieldLibIcon size={35} /></ShieldIcon>
          <Title>הפעלת אימות דו-שלבי</Title>
          <Subtitle>
            סרוק את קוד ה-QR עם אפליקציית האימות שלך
          </Subtitle>
        </Header>

        <QrSection>
          <QrImage src={setupData.qrCodeDataUrl} alt="QR Code לאימות דו-שלבי" />
        </QrSection>

        <SecretSection>
          <SecretLabel>אם QR לא נסרק — הזן את המפתח ידנית:</SecretLabel>
          <SecretCode>{setupData.secret}</SecretCode>
          <CopyHint
            tabIndex={0}
            role="button"
            aria-label="העתק מפתח"
            onClick={() => navigator.clipboard.writeText(setupData.secret)}
            onKeyDown={(e) => e.key === 'Enter' && navigator.clipboard.writeText(setupData.secret)}
          >
            <CopyIcon size={12} /> העתק
          </CopyHint>
        </SecretSection>

        <Divider />

        <OtpCodeInput
          onCodeChange={setCode}
          disabled={isLoading}
        />

        <Actions>
          <ConfirmBtn
            onClick={handleSubmit}
            disabled={!isComplete || isLoading}
            aria-label="אשר קוד"
          >
            {isLoading ? (
              <Spinner><SpinnerIcon size={15} /></Spinner>
            ) : (
              'אימות'
            )}
          </ConfirmBtn>
        </Actions>
      </Card>
    </Overlay>
  )
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1em;
`

const Card = styled.div`
  position: relative;
  background: #fff;
  border-radius: 24px;
  padding: 2em 2.2em 1.8em;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2em;
  box-shadow: 0 16px 48px rgba(41, 127, 118, 0.18);
`

const CloseBtn = styled.span`
  position: absolute;
  top: 1em;
  right: 1em;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e9f3f2;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #297f76;
  font-size: 1rem;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: tomato;
    color: #fff;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3em;
  text-align: center;
`

const ShieldIcon = styled.span`
  display: inline-flex;
  color: #297f76;
`

const Title = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a1a;
  direction: rtl;
`

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.5);
  direction: rtl;
`

const QrSection = styled.div`
  display: flex;
  justify-content: center;
`

const QrImage = styled.img`
  min-width: 200px;
  height: 200px;
  border-radius: 12px;
  border: 3px solid #e9f3f2;
`

const SecretSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4em;
  width: 100%;
`

const SecretLabel = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.5);
  direction: rtl;
`

const SecretCode = styled.code`
  background: #e9f3f2;
  color: #1e5e57;
  padding: 0.4em 0.9em;
  border-radius: 8px;
  font-size: 0.9rem;
  letter-spacing: 0.12em;
  font-weight: 600;
  word-break: break-all;
  text-align: center;
`

const CopyHint = styled.span`
  font-size: 0.78rem;
  color: #297f76;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.3em;
  transition: opacity 0.2s;
  &:hover { opacity: 0.7; }
`

const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid #e9f3f2;
  margin: 0;
`

const Actions = styled.div`
  display: flex;
  gap: 0.8em;
  align-items: center;
  margin-top: 0.4em;
`

const ConfirmBtn = styled.button`
  padding: 0.6em 1.8em;
  background: #297f76;
  color: #fff;
  border: none;
  border-radius: 20px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  gap: 0.4em;

  &:hover:not(:disabled) { background: #1e5e57; }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const Spinner = styled.span`
  display: inline-flex;
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
`

export default QrSetupModal
